export const DECISION_TABLE_NODE_TYPE = "decision_table";
export const DECISION_TABLE_SCHEMA_VERSION = 1;
export const DECISION_TABLE_TYPES = Object.freeze([
  "string",
  "integer",
  "decimal",
  "boolean",
  "date",
]);
export const DECISION_TABLE_PATHS = Object.freeze([
  { id: "matched", label: "Matched" },
  { id: "no_match", label: "No match" },
]);
export const DECISION_TABLE_LIMITS = Object.freeze({ inputs: 12, outputs: 12, rows: 200 });

const aliasPattern = /^[a-z][a-z0-9_]{0,63}$/;
const fieldKeyPattern = /^[a-z][a-z0-9_.-]{0,127}$/;
const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const createDecisionTableRowId = () => {
  const id =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID().replaceAll("-", "")
      : null;
  return `row_${id || `${Date.now()}_${Math.random().toString(16).slice(2)}`}`;
};

export const createDecisionTableConfig = () => ({
  schemaVersion: DECISION_TABLE_SCHEMA_VERSION,
  inputs: [
    {
      key: "input",
      sourceKind: "SessionValue",
      sourceFieldKey: "custom.response",
      dataType: "string",
    },
  ],
  outputs: [{ key: "result", fieldKey: "decision.result", dataType: "string" }],
  rows: [
    {
      rowId: createDecisionTableRowId(),
      cells: { input: { mode: "exact", value: "" } },
      outputs: { result: "" },
    },
  ],
});

const isTypedValue = (dataType, value) => {
  if (dataType === "string") return typeof value === "string" && value.length <= 512;
  if (dataType === "integer") return /^-?\d+$/.test(String(value ?? "")) && Number.isSafeInteger(Number(value));
  if (dataType === "decimal") return /^-?(?:\d+\.?\d*|\.\d+)$/.test(String(value ?? "")) && Number.isFinite(Number(value));
  if (dataType === "boolean") return value === true || value === false || value === "true" || value === "false";
  if (dataType === "date") return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
  return false;
};

export const coerceDecisionTableValue = (dataType, value) => {
  if (dataType === "integer") return Number.parseInt(String(value), 10);
  if (dataType === "decimal") return Number(String(value));
  if (dataType === "boolean") return value === true || value === "true";
  return String(value ?? "").trim();
};

export const normalizeDecisionTableConfig = config => ({
  schemaVersion: DECISION_TABLE_SCHEMA_VERSION,
  inputs: (config?.inputs || []).map(input => ({
    key: String(input?.key || "").trim().toLowerCase(),
    sourceKind: input?.sourceKind === "RequestContext"
      ? "RequestContext"
      : input?.sourceKind === "ContactCustomField"
        ? "ContactCustomField"
        : "SessionValue",
    ...(input?.sourceKind === "ContactCustomField" || input?.sourceKind === "RequestContext"
      ? { sourceFieldId: String(input?.sourceFieldId || "").trim() }
      : { sourceFieldKey: String(input?.sourceFieldKey || "").trim().toLowerCase() }),
    dataType: String(input?.dataType || "string").trim().toLowerCase(),
  })),
  outputs: (config?.outputs || []).map(output => ({
    key: String(output?.key || "").trim().toLowerCase(),
    fieldKey: String(output?.fieldKey || "").trim().toLowerCase(),
    dataType: String(output?.dataType || "string").trim().toLowerCase(),
  })),
  rows: (config?.rows || []).map(row => ({
    rowId: String(row?.rowId || "").trim(),
    cells: Object.fromEntries(
      (config?.inputs || []).map(input => {
        const key = String(input?.key || "").trim().toLowerCase();
        const cell = row?.cells?.[input?.key] ?? row?.cells?.[key] ?? { mode: "exact", value: "" };
        const mode = cell?.mode === "any" ? "any" : "exact";
        return [
          key,
          mode === "any"
            ? { mode: "any" }
            : { mode: "exact", value: coerceDecisionTableValue(input?.dataType || "string", cell?.value) },
        ];
      })
    ),
    outputs: Object.fromEntries(
      (config?.outputs || []).map(output => {
        const key = String(output?.key || "").trim().toLowerCase();
        return [key, coerceDecisionTableValue(output?.dataType || "string", row?.outputs?.[output?.key] ?? row?.outputs?.[key])];
      })
    ),
  })),
});

export const validateDecisionTableConfig = config => {
  if (Number(config?.schemaVersion) !== DECISION_TABLE_SCHEMA_VERSION) return { isValid: false, message: "Decision Table schema version is unsupported." };
  const inputs = Array.isArray(config?.inputs) ? config.inputs : [];
  const outputs = Array.isArray(config?.outputs) ? config.outputs : [];
  const rows = Array.isArray(config?.rows) ? config.rows : [];
  if (!inputs.length || inputs.length > DECISION_TABLE_LIMITS.inputs) return { isValid: false, message: `Decision Table requires 1-${DECISION_TABLE_LIMITS.inputs} inputs.` };
  if (!outputs.length || outputs.length > DECISION_TABLE_LIMITS.outputs) return { isValid: false, message: `Decision Table requires 1-${DECISION_TABLE_LIMITS.outputs} outputs.` };
  if (!rows.length || rows.length > DECISION_TABLE_LIMITS.rows) return { isValid: false, message: `Decision Table requires 1-${DECISION_TABLE_LIMITS.rows} rows.` };

  const inputKeys = inputs.map(input => String(input?.key || "").trim().toLowerCase());
  const outputKeys = outputs.map(output => String(output?.key || "").trim().toLowerCase());
  if (inputKeys.some(key => !aliasPattern.test(key)) || new Set(inputKeys).size !== inputKeys.length) return { isValid: false, message: "Input aliases must be unique lowercase identifiers." };
  if (outputKeys.some(key => !aliasPattern.test(key)) || new Set(outputKeys).size !== outputKeys.length) return { isValid: false, message: "Output aliases must be unique lowercase identifiers." };
  if (inputs.some(input => !DECISION_TABLE_TYPES.includes(input?.dataType))) return { isValid: false, message: "An input has an unsupported data type." };
  if (outputs.some(output => !DECISION_TABLE_TYPES.includes(output?.dataType))) return { isValid: false, message: "An output has an unsupported data type." };
  if (inputs.some(input => !["SessionValue", "ContactCustomField", "RequestContext"].includes(input?.sourceKind))) return { isValid: false, message: "An input has an unsupported source." };
  if (inputs.some(input => input?.sourceKind === "SessionValue" ? !fieldKeyPattern.test(String(input?.sourceFieldKey || "")) : !guidPattern.test(String(input?.sourceFieldId || "")))) return { isValid: false, message: "Every input must select a valid source." };
  const outputFields = outputs.map(output => String(output?.fieldKey || "").trim().toLowerCase());
  if (outputFields.some(key => !fieldKeyPattern.test(key)) || new Set(outputFields).size !== outputFields.length) return { isValid: false, message: "Output session field keys must be valid and unique." };

  const rowIds = rows.map(row => String(row?.rowId || ""));
  if (rowIds.some(id => !/^row_[A-Za-z0-9_-]{1,64}$/.test(id)) || new Set(rowIds).size !== rowIds.length) return { isValid: false, message: "Decision Table row IDs must be valid and unique." };
  for (const row of rows) {
    if (Object.keys(row?.cells || {}).length !== inputs.length || Object.keys(row?.outputs || {}).length !== outputs.length) return { isValid: false, message: "Every row must contain all input and output cells." };
    for (const input of inputs) {
      const cell = row.cells?.[input.key];
      if (!cell || !["exact", "any"].includes(cell.mode)) return { isValid: false, message: "Every input cell must use Exact or Any." };
      if (cell.mode === "any" && Object.prototype.hasOwnProperty.call(cell, "value")) return { isValid: false, message: "Any cells cannot contain a value." };
      if (cell.mode === "exact" && !isTypedValue(input.dataType, cell.value)) return { isValid: false, message: `An Exact cell is invalid for ${input.dataType}.` };
    }
    for (const output of outputs) if (!isTypedValue(output.dataType, row.outputs?.[output.key])) return { isValid: false, message: `A row output is invalid for ${output.dataType}.` };
  }
  return { isValid: true, message: "" };
};
